import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { PageHeader } from "@/components/data-display/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Field, Input } from "@/components/ui/input";
import { LoadingState, NotAvailableState } from "@/components/ui/system-state";
import { AdminConsoleFailureState } from "@/features/admin/admin-console-failure";
import {
  SUPPORT_REFERENCE_INPUT,
  outcomeTone,
  searchTrace,
  type AdminConsoleFailure,
  type TraceEvent,
  type TraceSearch,
} from "@/lib/admin-console-api";

/**
 * D1 — Trace lookup (ALL-5773, spec: product-design/specs/04-governance-evidence/
 * admin-support-diagnostics-spec.md). A support admin pastes the support reference
 * a user saw and learns which service failed, on which route, why, which fields
 * were wrong and what to do next. Reads GET /admin/api/diagnostics/search through
 * the browser session; the server decides access. Read-only.
 */

type LookupState =
  | { kind: "idle" }
  | { kind: "loading"; reference: string }
  | { kind: "done"; reference: string; result: TraceSearch }
  | { kind: "failed"; reference: string; failure: AdminConsoleFailure };

function formatTime(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function humanize(token: string): string {
  const text = token.replace(/[_-]+/g, " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : token;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-0.5 py-1.5 sm:grid-cols-[150px_1fr] sm:gap-3">
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-sm text-ink">{children}</dd>
    </div>
  );
}

function TraceEventCard({ event }: { event: TraceEvent }) {
  const tone = outcomeTone(event.outcome);
  const route = event.httpRoute ?? event.routeClass;
  const routeText = route
    ? `${event.httpMethod ? `${event.httpMethod} ` : ""}${route}${event.httpStatus ? ` → ${event.httpStatus}` : ""}`
    : undefined;
  return (
    <Card data-testid="trace-event">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs text-muted">{event.sourceService ?? "Unknown service"}</div>
            <h3 className="break-words font-mono text-base font-bold text-ink">{event.reasonCode ?? event.outcome ?? "No reason recorded"}</h3>
          </div>
          <Badge variant={tone}>{event.outcome ? humanize(event.outcome) : "Unknown outcome"}</Badge>
        </div>
        <dl className="mt-3 divide-y divide-line">
          <Row label="When">{formatTime(event.occurredAt)}</Row>
          <Row label="Service">{event.sourceService ?? "—"}</Row>
          {(event.eventFamily || event.eventType) && (
            <Row label="Event">{[event.eventFamily, event.eventType].filter(Boolean).join(" · ")}</Row>
          )}
          {routeText && (
            <Row label="Route">
              <code dir="ltr" className="font-mono text-[13px]">{routeText}</code>
            </Row>
          )}
          {event.failingFields.length > 0 && (
            <Row label="Failing fields">
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {event.failingFields.map((field) => (
                  <li key={field}>
                    <code dir="ltr" className="font-mono text-[13px]">{field}</code>
                    {event.failingFieldCodes[field] && (
                      <span className="ml-1 text-xs text-muted">{event.failingFieldCodes[field]}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Row>
          )}
          {event.clientRequestId && (
            <Row label="Correlation ID"><code dir="ltr" className="font-mono text-[13px]">{event.clientRequestId}</code></Row>
          )}
          {event.traceId && (
            <Row label="Trace ID"><code dir="ltr" className="font-mono text-[13px]">{event.traceId}</code></Row>
          )}
          {event.requestId && (
            <Row label="Request ID"><code dir="ltr" className="font-mono text-[13px]">{event.requestId}</code></Row>
          )}
          <Row label="Next step">
            {event.remediationCode ? humanize(event.remediationCode) : "Escalate to Alloy support with this support reference."}
          </Row>
        </dl>
      </CardBody>
    </Card>
  );
}

function TraceResults({ reference, result }: { reference: string; result: TraceSearch }) {
  if (result.status === "degraded") {
    return (
      <NotAvailableState
        title="The diagnostics source could not be reached"
        description="This lookup did not run, so it says nothing about whether events exist for this reference. Try again shortly; if it persists, contact Alloy support."
        reason={result.reason ? `Reason: ${result.reason}` : undefined}
      />
    );
  }
  if (result.events.length === 0) {
    return (
      <div data-testid="trace-empty" className="rounded-lg border border-line bg-surface px-4 py-10 text-center">
        <p className="text-sm font-semibold text-ink">No events were recorded for {reference}</p>
        <p className="mx-auto mt-1 max-w-[460px] text-sm text-muted">
          Check the reference with the person who reported it. A reference from another organization, or one older than the
          retention window, also returns nothing.
        </p>
        {result.notFoundCode && (
          <p className="mt-2 font-mono text-[11px] text-subtle" data-testid="trace-not-found-code">
            Code: {result.notFoundCode}
          </p>
        )}
      </div>
    );
  }
  return (
    <section aria-label="Lookup results" className="flex flex-col gap-3">
      <p className="text-sm text-muted">
        {result.returnedCount === 1 ? "1 event" : `${result.returnedCount} events`} for <code dir="ltr" className="font-mono">{reference}</code>
      </p>
      {result.events.map((event) => (
        <TraceEventCard key={event.id} event={event} />
      ))}
      {result.dataBoundary && <p className="text-xs text-muted">{result.dataBoundary}</p>}
    </section>
  );
}

export default function AdminTraceLookupPage() {
  const [params, setParams] = useSearchParams();
  const initialRef = (params.get("ref") ?? "").trim();
  const [input, setInput] = useState(initialRef);
  const [inputError, setInputError] = useState<string | null>(null);
  const [state, setState] = useState<LookupState>({ kind: "idle" });

  const run = useCallback(async (reference: string) => {
    setState({ kind: "loading", reference });
    const result = await searchTrace(reference);
    setState(result.ok ? { kind: "done", reference, result: result.data } : { kind: "failed", reference, failure: result });
  }, []);

  useEffect(() => {
    if (initialRef && SUPPORT_REFERENCE_INPUT.test(initialRef)) void run(initialRef);
    // Runs once for a deep link (?ref=); later searches go through submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const reference = input.trim();
    if (!reference) {
      setInputError("Enter the support reference the person saw, for example sr-01a0f24a.");
      return;
    }
    if (!SUPPORT_REFERENCE_INPUT.test(reference)) {
      setInputError("A support reference uses only letters, numbers, dots, dashes and colons.");
      return;
    }
    setInputError(null);
    setParams({ ref: reference }, { replace: true });
    void run(reference);
  };

  return (
    <div>
      <PageHeader
        title="Trace lookup"
        subtitle="Paste a support reference to see which service failed, why, and what to do next."
      />

      <form onSubmit={submit} className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end" noValidate>
        <div className="w-full sm:max-w-[420px]">
          <Field label="Support reference" htmlFor="trace-ref" error={inputError ?? undefined}>
            <Input
              id="trace-ref"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="sr-01a0f24a"
              autoComplete="off"
              spellCheck={false}
              error={!!inputError}
              leadingIcon={<Search className="size-4" />}
              aria-invalid={inputError ? true : undefined}
            />
          </Field>
        </div>
        <Button type="submit" loading={state.kind === "loading"} disabled={state.kind === "loading"}>
          Look up
        </Button>
      </form>

      {state.kind === "idle" && (
        <div className="rounded-lg border border-dashed border-line bg-bg px-4 py-10 text-center text-sm text-muted">
          Support references appear on error pages and in failed API responses. Ask the person for the one they saw.
        </div>
      )}
      {state.kind === "loading" && <LoadingState rows={3} />}
      {state.kind === "failed" && (
        <AdminConsoleFailureState failure={state.failure} subject="trace lookup" onRetry={() => void run(state.reference)} />
      )}
      {state.kind === "done" && <TraceResults reference={state.reference} result={state.result} />}
    </div>
  );
}
